import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      navigate("/login");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        await api.post("/auth/session", { session_id: sessionId });

        // create_session returns a MINIMAL user (user_id/email/name/picture)
        // — it does NOT include personality_profile/subscription. Hydrate the
        // full user via /auth/me so (a) the auth context has the real profile
        // and (b) the routing decision below sees a completed questionnaire.
        // This also verifies the HttpOnly session_token cookie round-trips —
        // if it was silently dropped (prod CORS / SameSite / domain mismatch),
        // refresh() returns null and we surface a clear error.
        const fullUser = await refresh();
        if (!fullUser) {
          // eslint-disable-next-line no-alert
          alert(
            "Sign-in completed but your browser rejected the session cookie.\n\n" +
              "On production this usually means the backend CORS / cookie domain " +
              "is mis-configured. Try a different browser or contact support."
          );
          navigate("/login", { replace: true });
          return;
        }

        // Returning founders with a completed personality profile land on
        // the dashboard directly. New users (or anyone who hasn't filled
        // / skipped the questionnaire yet) drop into /profile so the
        // wizard runs once before the dashboard.
        const pp = fullUser.personality_profile || {};
        const profileDone = Boolean(pp.updated_at || pp.skipped_at || pp.age_range);
        const dest = profileDone ? "/dashboard" : "/profile";
        window.history.replaceState(null, "", dest);
        navigate(dest, { state: { user: fullUser }, replace: true });
      } catch (e) {
        // Most common reason here: the emergent auth backend rejected the
        // session_id (expired or redirect-URI not whitelisted).
        const msg =
          e?.response?.status === 401
            ? "Sign-in expired. Please try again."
            : e?.response?.data?.detail || "Sign-in failed. Please try again.";
        // eslint-disable-next-line no-alert
        alert(msg);
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refresh]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" data-testid="auth-callback">
      <div className="font-display text-2xl tracking-tighter">Signing you in…</div>
    </div>
  );
}
